import { useState, useEffect, useCallback } from 'react';
import { activityService } from '../services/activityService';
import { userService } from '../services/userService';
import type { Activity, ActivityFilters } from '../types/activity';
import type { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';

interface UseActivitiesOptions {
  filters?: ActivityFilters;
  initialLimit?: number;
}

interface UseActivitiesResult {
  activities: Activity[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useActivities(
  userId: string | null,
  options: UseActivitiesOptions = {}
): UseActivitiesResult {
  const { filters, initialLimit = 20 } = options;

  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | undefined>();
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchActivities = useCallback(
    async (reset: boolean = false) => {
      if (!userId) {
        setActivities([]);
        setLoading(false);
        setError(null);
        setHasMore(false);
        return;
      }

      try {
        if (reset) {
          setLoading(true);
          setLastDoc(undefined);
        }

        // Get list of users the current user is following
        const { following } = await userService.getUserFollowing(userId, 1000); // Get all following users
        const followingIds = following.map((f) => f.uid);

        // If not following anyone, return empty
        if (followingIds.length === 0) {
          setActivities([]);
          setHasMore(false);
          setLoading(false);
          return;
        }

        // Fetch activities from followed users
        const result = await activityService.getActivitiesFromUsers(
          followingIds,
          filters,
          {
            limit: initialLimit,
            lastDoc: reset ? undefined : lastDoc
          }
        );

        if (reset) {
          setActivities(result.activities);
        } else {
          setActivities(prev => [...prev, ...result.activities]);
        }

        setHasMore(result.hasMore);
        setLastDoc(result.lastDoc);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching activities:', err);
        setError(err.message || 'Failed to fetch activities');
      } finally {
        setLoading(false);
        setIsLoadingMore(false);
      }
    },
    [userId, filters, initialLimit, lastDoc]
  );

  // Initial load
  useEffect(() => {
    fetchActivities(true);
  }, [userId, filters?.category]); // Re-fetch when userId or category filter changes

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    await fetchActivities(false);
  }, [isLoadingMore, hasMore, fetchActivities]);

  const refresh = useCallback(async () => {
    await fetchActivities(true);
  }, [fetchActivities]);

  return {
    activities,
    loading,
    error,
    hasMore,
    loadMore,
    refresh
  };
}
